import "./PortalDeviceTiles.scss";

import { FC } from "react";

import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck";
import { BasketSummaryResponse } from "@/services/model";
import { TransformedProducts } from "@/utils/tariffUtils.ts";

import { usePortalDeviceTiles } from "./hooks/usePortalDeviceTiles.ts";
import { TilesGrid } from "./TilesGrid/TilesGrid";

interface PortalDeviceTilesProps {
  manageRoute: string;
  addDeviceRoute?: string;
  showMetaTiles?: boolean; // Show meta tiles (Add Device, Upsell; default: true)
  showPromo?: boolean; // Show itemized bill promo tile (default: true)
  showSims?: boolean; // Show SIM tiles (default: true)
  showPurchaseIntents?: boolean; // Show purchase intent tiles (default: false)
  basketItems?: BasketSummaryResponse[]; // Purchase intent items
  useInfiniteScroll?: boolean; // Whether to use infinite scrolling (default: true)
  fetchAllPages?: boolean; // Fetch all pages when infinite scroll is disabled (default: true)
  maxTiles?: number; // Limit the number of SIM tiles rendered
  pageSize?: number; // Page size for the SIM list query
  /** Client-side priority sort within the fetch cap (default: false; opt in per page). */
  sortByDisplayPriority?: boolean;
  isLoadingIntents?: boolean; // Whether purchase intents are loading
  processedOptions?: TransformedProducts; // Pre-fetched options
}

/**
 * The main component for displaying the device tiles in the portal.
 * It manages the fetching of SIM card data, promo visibility, and infinite scrolling.
 */
export const PortalDeviceTiles: FC<PortalDeviceTilesProps> = ({
  manageRoute,
  addDeviceRoute,
  showMetaTiles = true,
  showPromo = true,
  showSims = true,
  showPurchaseIntents = false,
  basketItems,
  useInfiniteScroll = true,
  fetchAllPages = true,
  maxTiles,
  pageSize: pageSizeOverride,
  sortByDisplayPriority = false,
  isLoadingIntents = false,
  processedOptions,
}) => {
  const needsIdCheck = useNeedsIDCheck();
  const {
    flatSimList,
    flatExtendedSimList,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    hasNextPage,
    shouldShowItemizedBillPromoCard,
    pageSize,
  } = usePortalDeviceTiles(showPromo, showSims, {
    pageSize: pageSizeOverride,
    sortByDisplayPriority,
  });

  const displayIsLoading = showSims ? isLoading : isLoadingIntents;

  return (
    <TilesGrid
      flatSimList={flatSimList}
      flatExtendedSimList={flatExtendedSimList}
      basketItems={basketItems}
      isLoading={displayIsLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      isError={isError}
      shouldShowItemizedBillPromoCard={shouldShowItemizedBillPromoCard}
      showMetaTiles={showMetaTiles}
      showSims={showSims}
      showPurchaseIntents={showPurchaseIntents}
      addDeviceRoute={addDeviceRoute}
      needsIdCheck={needsIdCheck}
      manageRoute={manageRoute}
      pageSize={pageSize}
      useInfiniteScroll={useInfiniteScroll}
      fetchAllPages={fetchAllPages}
      maxTiles={maxTiles}
      processedOptions={processedOptions}
    />
  );
};
